import { environment } from '../../../environments/default/environment';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse, HttpContext } from '@angular/common/http';
import { Observable, of, throwError, BehaviorSubject, timer } from 'rxjs';
import { map, catchError, tap, first, retry, switchMap } from 'rxjs/operators';

import { MPIBookService } from '../../api/proxy/mpi/services/mpi-book.service';
import { BookBookRegistrationIdUsernameGet$Params } from '../../api/proxy/mpi/fn/book/book-book-registration-id-username-get';
import { BookRegisterBookRegistrationIdPost$Params } from '../../api/proxy/mpi/fn/book/book-register-book-registration-id-post';
import { bookBookRegistrationIdGet } from '../../api/proxy/mpi/fn/book/book-book-registration-id-get';
import { BookRegistrationDto } from '../../api/proxy/mpi/models/book-registration-dto';
import { RegisterUserDto } from '../../api/proxy/mpi/models/register-user-dto';
import { MPIStrictHttpResponse } from '../../api/proxy/mpi/mpi-strict-http-response';

import { AuthLocalAccountService } from '../../api/proxy/auth/services/auth-local-account.service';
import { AccessTokenModel } from '../../api/proxy/auth/models/access-token-model';
import { LoginRequestModel } from '../../api/proxy/auth/models/login-request-model';
import { RequestOtpModel } from '../../api/proxy/auth/models/request-otp-model';
import { RequestOtpResponseModel } from '../../api/proxy/auth/models/request-otp-response-model';
import { VerifyOtpRequestModel } from '../../api/proxy/auth/models/verify-otp-request-model';
import { ForgotPasswordRequestModel } from '../../api/proxy/auth/models/forgot-password-request-model';
import { ChangePasswordRequestModel } from '../../api/proxy/auth/models/change-password-request-model';
import { UpdateProfileRequestModel } from '../../api/proxy/auth/models/update-profile-request-model';
import { ResetPasswordRequestModel } from '../../api/proxy/auth/models/reset-password-request-model';
import { RefreshTokenRequestModel } from '../../api/proxy/auth/models/refresh-token-request-model';
import { RefreshTokenResponseModel } from '../../api/proxy/auth/models/refresh-token-response-model';
import { HasLogin } from '@/api/proxy/mpi/models/has-login'; 

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _isAuthenticated = new BehaviorSubject<boolean>(false);
  isAuthenticated$ = this._isAuthenticated.asObservable();
  public prefillData = new BehaviorSubject<BookRegistrationDto | null>(null);
  prefillData$ = this.prefillData.asObservable();
  private loggedInUsernameSubject = new BehaviorSubject<string | null>(null);
  public loggedInUsername$ = this.loggedInUsernameSubject.asObservable();
  private mpiRootUrl = environment.mpiBaseUrl;
  private readonly ACCESS_TOKEN_KEY = 'auth_access_token';
  private readonly REFRESH_TOKEN_KEY = 'auth_refresh_token';
  private readonly LOGGED_IN_USERNAME_KEY = 'auth_logged_in_username';
  private readonly BOOKING_USER_ASSOCIATIONS_KEY = 'booking_user_associations';
  public readonly applicationId = environment.clientId;
  public readonly applicationScope = environment.scope;
  public LoginStatus = HasLogin; 

  public logoUri: string = '';

  constructor(
    private router: Router,
    private http: HttpClient,
    private mpiBookService: MPIBookService,
    private authLocalAccountService: AuthLocalAccountService
  ) {
    this._isAuthenticated.next(!!this.getAccessToken());
    const storedUsername = localStorage.getItem(this.LOGGED_IN_USERNAME_KEY);
    if (storedUsername && this.isAuthenticated()) {
      this.loggedInUsernameSubject.next(storedUsername);
    }
   
  }

  public getUsernameFromDto(data: BookRegistrationDto): string | null {
    const patientDetails = data?.bookRegistrationRequest?.patient;
    if (!patientDetails) {
      return null;
    }
    return patientDetails.southAfricanIdNumber || patientDetails.passportNumber || null;
  }

  private getBookingUserAssociations(): { [bookingId: string]: string } {
   // const associations = localStorage.getItem(this.BOOKING_USER_ASSOCIATIONS_KEY);
    //return associations ? JSON.parse(associations) : {};
    return {};
  
  }

  private saveBookingUserAssociations(associations: { [bookingId: string]: string }): void {
    //localStorage.setItem(this.BOOKING_USER_ASSOCIATIONS_KEY, JSON.stringify(associations));
  }

  public handleBookingDataResponse(dto: BookRegistrationDto | null): void {
    if (!dto || !dto.bookRegistrationId) {
      console.error('AuthService: Invalid DTO or missing bookRegistrationId for handling response. DTO:', dto);
      this.router.navigate(['/invalid-link'], { queryParams: { reason: 'invalid-dto-response' } });
      return;
    }

    this.setPrefillData(dto);

    switch (dto.hasLogin) {
      case HasLogin.True:
        if (dto.login) {
        
          this.router.navigate(['/login'], {
            queryParams: {
              username: dto.login,
              bookRegistrationId: dto.bookRegistrationId,
            },
          });
        } else {
          console.warn(`AuthService: hasLogin is TRUE but 'login' field (username) is missing for bookingId: ${dto.bookRegistrationId}. The calling component should handle this by prompting for identity.`);
          // NO NAVIGATION HERE - Let LandingComponent show identity input
        }
        break;
      case HasLogin.False:
       
        this.router.navigate(['/register']);
        break;
      case HasLogin.Unknown:
       
        // *** NO NAVIGATION HERE FOR UNKNOWN ***
        break;
      default:
        let statusString = String(dto.hasLogin);
        // Simple check if it's a number and might be an unmapped enum value
        if (typeof dto.hasLogin === 'number' && HasLogin[dto.hasLogin as number]) {
            statusString = HasLogin[dto.hasLogin as number];
        }
        console.error(`AuthService: Unhandled or invalid hasLogin status '${statusString}' for bookingId: ${dto.bookRegistrationId}.`);
        this.router.navigate(['/login'], { queryParams: { reason: 'unknown_or_invalid_login_status' } });
    }
  }
  public clearSpecificBookingAssociation(bookingId: string): void {
  if (!bookingId) {
    console.warn('AuthService: Cannot clear specific booking association, bookingId is missing.');
    return;
  }
  const associations = this.getBookingUserAssociations();
  if (associations[bookingId]) {
    delete associations[bookingId];
    this.saveBookingUserAssociations(associations);
    
  } 
}
  associateBookingIdWithUsername(bookingId: string, username: string): void {
    if (!bookingId || !username) {
      console.warn('AuthService: Cannot associate booking ID with username due to missing bookingId or username.');
      return;
    }
    const associations = this.getBookingUserAssociations();
    if (associations[bookingId] !== username) {
      associations[bookingId] = username;
      this.saveBookingUserAssociations(associations);

    } 
  }

  getAssociatedUsernameForBooking(bookingId: string): string | null {
    if (!bookingId) return null;
    const associations = this.getBookingUserAssociations();
    return associations[bookingId] || null;
  }

  validateBookingForCurrentUser(bookingId: string): boolean {
    if (!bookingId) return false;
    const associatedUsername = this.getAssociatedUsernameForBooking(bookingId);
    const currentUser = this.getCurrentLoggedInUsername();
    const isValid = !!associatedUsername && !!currentUser && associatedUsername === currentUser;
 
    return isValid;
  }

  setPrefillData(data: BookRegistrationDto | null): void {
    this.prefillData.next(data);
    if (data && data.bookRegistrationId) {
      const usernameFromDto = this.getUsernameFromDto(data);
      if (usernameFromDto) {
        this.associateBookingIdWithUsername(data.bookRegistrationId, usernameFromDto);
      } else {
        
      }
    }
  }

  clearPrefillData(): void {
    this.prefillData.next(null);
  }

  public getCurrentLoggedInUsername(): string | null {
    return this.loggedInUsernameSubject.value;
  }

  getUserInfoByPin(pin: string): Observable<BookRegistrationDto | null> {
  
    return this.mpiBookService.bookPinPinGet$Response({ pin }).pipe(
      map((response: MPIStrictHttpResponse<BookRegistrationDto>) => {
        const apiDto = response.body;
        if (!apiDto || !apiDto.bookRegistrationRequest || !apiDto.bookRegistrationId) {
          console.warn('AUTH SERVICE: API response body (BookRegistrationDto, nested request, or bookRegistrationId) was empty for PIN lookup.');
          this.clearPrefillData();
          return null;
        }
     
        this.setPrefillData(apiDto);
        return apiDto;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('AUTH SERVICE: Error fetching user info by PIN from API:', error);
        this.clearPrefillData();
        if (error.status === 404) {
     
          return of(null);
        }
        return throwError(() => error);
      })
    );
  }

  getUserInfoByGuid(guid: string): Observable<BookRegistrationDto | null> {
   
    return bookBookRegistrationIdGet(this.http, this.mpiRootUrl, { bookRegistrationId: guid }).pipe(
      map((response: MPIStrictHttpResponse<BookRegistrationDto>) => {
        const apiDto = response.body;
        if (!apiDto || !apiDto.bookRegistrationRequest || !apiDto.bookRegistrationId) {
          console.warn('AUTH SERVICE: API response body (BookRegistrationDto, nested request, or bookRegistrationId) was empty for GUID lookup.');
          this.clearPrefillData();
          return null;
        }
        
        this.setPrefillData(apiDto);
        return apiDto;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('AUTH SERVICE: Error fetching user info by GUID from API:', error);
        this.clearPrefillData();
        if (error.status === 404) {
 
          return of(null);
        }
        return throwError(() => error);
      })
    );
  }

  getUserInfoByGuidAndUsername(guid: string, username: string): Observable<BookRegistrationDto | null> {

    const params: BookBookRegistrationIdUsernameGet$Params = {
      bookRegistrationId: guid,
      username: username,
    };

    return this.mpiBookService.bookBookRegistrationIdUsernameGet$Response(params).pipe(
      map((response: MPIStrictHttpResponse<BookRegistrationDto>) => {
        const apiDto = response.body;
        if (!apiDto || !apiDto.bookRegistrationRequest || !apiDto.bookRegistrationId) {
          console.warn(
            `AUTH SERVICE: API response body (BookRegistrationDto, nested request, or bookRegistrationId) was empty for GUID+Username lookup (${guid}, ${username}).`
          );
          this.clearPrefillData();
          return null;
        }
       
        this.setPrefillData(apiDto);
        return apiDto;
      }),
      catchError((error: HttpErrorResponse) => {
        console.error(`AUTH SERVICE: Error fetching user info by GUID+Username (${guid}, ${username}) from API:`, error);
        this.clearPrefillData();
        if (error.status === 404) {
          
          return of(null);
        }
        return throwError(() => error);
      })
    );
  }

  login(loginRequest: LoginRequestModel, context?: HttpContext): Observable<AccessTokenModel> {
    const requestWithDefaults: LoginRequestModel = {
      ...loginRequest,
      applicationId: loginRequest.applicationId || this.applicationId,
      applicationScope: loginRequest.applicationScope || this.applicationScope,
    };

    if (
      !requestWithDefaults.userName ||
      !requestWithDefaults.password ||
      !requestWithDefaults.applicationId ||
      !requestWithDefaults.applicationScope
    ) {
      console.error('AUTH SERVICE: Login request is missing required fields.', requestWithDefaults);
      return throwError(() => new Error('Login request is missing required fields.'));
    }


    return this.authLocalAccountService.localAccountLoginUserPost({ body: requestWithDefaults }, context).pipe(
      switchMap((tokens: AccessTokenModel) => {
        this.storeTokens(tokens);
        this._isAuthenticated.next(true);
        const loggedInUsername = requestWithDefaults.userName!;
        this.loggedInUsernameSubject.next(loggedInUsername);
        localStorage.setItem(this.LOGGED_IN_USERNAME_KEY, loggedInUsername);
   

        const currentPrefillData = this.prefillData.value;
        let refreshLookupNeeded = false;
        let bookingIdForRefresh: string | null = null;

        if (currentPrefillData && currentPrefillData.bookRegistrationId) {
          bookingIdForRefresh = currentPrefillData.bookRegistrationId;
       

          this.associateBookingIdWithUsername(bookingIdForRefresh, loggedInUsername);

          const usernameFromExistingDto = this.getUsernameFromDto(currentPrefillData);
          if (!usernameFromExistingDto) {
           
            refreshLookupNeeded = true;
          }
        } else {

        }

        if (refreshLookupNeeded && bookingIdForRefresh) {
         
          return this.getUserInfoByGuidAndUsername(bookingIdForRefresh, loggedInUsername).pipe(
            tap((refreshedDto) => {
              if (refreshedDto) {
              
              } else {
                console.warn(`AUTH SERVICE: Post-login refresh lookup for booking ${bookingIdForRefresh} returned null or failed.`);
              }
            }),
            map(() => tokens),
            catchError((err) => {
              console.error(`AUTH SERVICE: Error during post-login refresh lookup for ${bookingIdForRefresh}. Login still successful.`, err);
              return of(tokens);
            })
          );
        } else {
          return of(tokens);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        this._isAuthenticated.next(false);
        this.loggedInUsernameSubject.next(null);
        localStorage.removeItem(this.LOGGED_IN_USERNAME_KEY);
        console.error('AUTH SERVICE: Login API call failed', error);
        this.removeTokens();
        return throwError(() => error);
      })
    );
  }

  register(bookRegistrationId: string, registerRequest: RegisterUserDto, context?: HttpContext): Observable<void> {
    if (!bookRegistrationId) {
      console.error('AUTH SERVICE: bookRegistrationId is required for registration.');
      return throwError(() => new Error('Book Registration ID is required.'));
    }
 
    const params: BookRegisterBookRegistrationIdPost$Params = {
      bookRegistrationId: bookRegistrationId,
      body: registerRequest,
    };

    return this.mpiBookService.bookRegisterBookRegistrationIdPost(params, context).pipe(
      tap(() => {
       
        if (registerRequest.userName) {
          this.associateBookingIdWithUsername(bookRegistrationId, registerRequest.userName);
        }
      }),
      catchError((error: HttpErrorResponse) => {
        console.error('AUTH SERVICE: MPI Registration failed', error);
        return throwError(() => error);
      })
    );
  }

  requestOtp(otpRequest: RequestOtpModel, context?: HttpContext): Observable<RequestOtpResponseModel> {
    const MAX_RETRIES = 3;
    const RETRY_DELAY_MS = 2000;

    return this.authLocalAccountService.localAccountRequestOtpPost({ body: otpRequest }, context).pipe(
      switchMap((response: RequestOtpResponseModel) => {
        if (response.requested === false && response.errorMessage === 'Unable to send OTP') {
          const errorToRetry = new Error('OTP_SEND_FAILED_RETRYABLE') as any;
          errorToRetry.originalResponse = response;
          errorToRetry.isRetryableOtpError = true;
          return throwError(() => errorToRetry);
        }
        return of(response);
      }),
      retry({
        count: MAX_RETRIES,
        delay: (error: any, retryCount: number) => {
          if (error.isRetryableOtpError) {
            
            return timer(RETRY_DELAY_MS);
          }
          return throwError(() => error);
        },
      }),
      catchError((error: any) => {
        if (error.isRetryableOtpError && error.originalResponse) {
          console.error(
            `AUTH SERVICE: Request OTP failed permanently after ${MAX_RETRIES} retries for "Unable to send OTP". Returning original error response.`
          );
          return of(error.originalResponse as RequestOtpResponseModel);
        }
        console.error('AUTH SERVICE: Request OTP failed with non-retryable error or other issue:', error);
        return throwError(() => error);
      })
    );
  }

  verifyOtp(verifyRequest: VerifyOtpRequestModel, context?: HttpContext): Observable<boolean> {
    return this.authLocalAccountService.localAccountVerifyOtpAsyncPost({ body: verifyRequest }, context).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('AUTH SERVICE: Verify OTP failed', error);
        return throwError(() => error);
      })
    );
  }

  refreshToken(tokenRequest?: RefreshTokenRequestModel, context?: HttpContext): Observable<RefreshTokenResponseModel> {
    const currentRefreshToken = this.getRefreshToken();
    const appId = tokenRequest?.applicationId ?? this.applicationId;

    if (!currentRefreshToken) {
      console.error('AUTH SERVICE: Refresh token is missing.');
      this.logout(true);
      return throwError(() => new Error('No refresh token available. Please login again.'));
    }
    if (!appId) {
      console.error('AUTH SERVICE: Application ID is missing or invalid for refresh token request.');
      this.logout(true);
      return throwError(() => new Error('Valid Application ID required for refresh token'));
    }

    const requestBody: RefreshTokenRequestModel = {
      refreshToken: currentRefreshToken,
      applicationId: appId,
    };

    return this.authLocalAccountService.localAccountRefreshTokenPost({ body: requestBody }, context).pipe(
      tap((tokens: RefreshTokenResponseModel) => {
        this.storeTokens(tokens);
        this._isAuthenticated.next(true);

      }),
      catchError((error: HttpErrorResponse) => {
        console.error('AUTH SERVICE: Token refresh failed', error);
        this.logout(true);
        return throwError(() => error);
      })
    );
  }

  logout(navigateToLogin: boolean = true): void {

    this.removeTokens();
    localStorage.removeItem(this.LOGGED_IN_USERNAME_KEY);
    this.loggedInUsernameSubject.next(null);
    this._isAuthenticated.next(false);
    this.clearPrefillData();
    localStorage.removeItem(this.BOOKING_USER_ASSOCIATIONS_KEY);


    if (navigateToLogin) {
      this.router.navigate(['/login']);
    }
  }

  isAuthenticated(): boolean {
    return this._isAuthenticated.value;
  }

  changePassword(passwordRequest: ChangePasswordRequestModel, context?: HttpContext): Observable<void> {
    return this.authLocalAccountService.localAccountChangePasswordPost({ body: passwordRequest }, context).pipe(
   
      catchError((error: HttpErrorResponse) => {
        console.error('AUTH SERVICE: Change password failed', error);
        return throwError(() => error);
      })
    );
  }

  updateProfile(profileRequest: UpdateProfileRequestModel, context?: HttpContext): Observable<void> {
    return this.authLocalAccountService.localAccountUpdateProfilePost({ body: profileRequest }, context).pipe(

      catchError((error: HttpErrorResponse) => {
        console.error('AUTH SERVICE: Update profile failed', error);
        return throwError(() => error);
      })
    );
  }

  requestForgotPassword(forgotRequest: ForgotPasswordRequestModel, context?: HttpContext): Observable<void> {
    return this.authLocalAccountService.localAccountForgotPasswordRequestPost({ body: forgotRequest }, context).pipe(
 
      catchError((error: HttpErrorResponse) => {
        console.error('AUTH SERVICE: Forgot password request failed', error);
        return throwError(() => error);
      })
    );
  }

  resetForgotPassword(resetRequest: ResetPasswordRequestModel, context?: HttpContext): Observable<void> {
    return this.authLocalAccountService.localAccountResetForgotPasswordPost({ body: resetRequest }, context).pipe(
      
      catchError((error: HttpErrorResponse) => {
        console.error('AUTH SERVICE: Reset forgot password failed', error);
        return throwError(() => error);
      })
    );
  }

  private storeTokens(tokens: AccessTokenModel | RefreshTokenResponseModel): void {
    if (typeof tokens.access_token === 'string' && tokens.access_token.length > 0) {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.access_token);

    }
    if ('refresh_token' in tokens && typeof tokens.refresh_token === 'string' && tokens.refresh_token.length > 0) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refresh_token);

    }
  }

  private removeTokens(): void {
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
  }

  getAccessToken(): string | null {
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }
}
